import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NodeAPI from "../api";
import { useAuth } from "../context/AuthContext";
import { Box, Card, CardContent, Typography, Button, CircularProgress,TextField} from "@mui/material";
import { isAnswerCorrect } from "../components/semanticChecker"; // adjust path
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";

const Feedback = () => {
    const { topicId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
 const { width, height } = useWindowSize(); // Get screen size for Confetti

    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isCorrect, setIsCorrect] = useState(null);
    const [score, setScore] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFinalScore, setShowFinalScore] = useState(false);
const [typedAnswer, setTypedAnswer] = useState(""); // For structured input

    useEffect(() => {
        if (!user) return;
        setLoading(true);
        NodeAPI.get(`/quiz/quiz/interactive/${topicId}?userId=${user.id}`)
            .then(({ data }) => {
                setQuestions(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching questions:", err);
                setError("Failed to load questions.");
                setLoading(false);
            });
    }, [topicId, user]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: "center", mt: 4 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!questions.length) {
        return (
            <Box sx={{ textAlign: "center", mt: 4 }}>
                <Typography>No questions found for this topic.</Typography>
            </Box>
        );
    }

  const handleAnswerSelection = async (answer) => {
    const currentQuestion = questions[currentQuestionIndex];
    const correctAnswer = currentQuestion.correct_answer;
    const hasOptions = currentQuestion.options?.length > 0;

    let correct = false;

    if (hasOptions) {
        // Multiple choice or true/false
        correct = answer.trim().toLowerCase() === correctAnswer.trim().toLowerCase();
    } else {
        // Structured (free text) — use AI validation
        try {
            correct = await isAnswerCorrect(answer, correctAnswer);
        } catch (err) {
            console.error("AI validation failed:", err);
        }
    }

    setSelectedAnswer(answer);
    setIsCorrect(correct);

    if (correct) {
        setScore((prev) => prev + 1);
    }

    // Move to next question after 2s
    setTimeout(() => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1);
            setSelectedAnswer(null);
            setIsCorrect(null);
            setTypedAnswer(""); // Reset input for structured
        } else {
            setShowFinalScore(true);
        }
    }, 3000);
};

 const scorePercentage = (score / questions.length) * 100;
  const isHighScore = scorePercentage >= 80;

    if (showFinalScore) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 5, textAlign: "center" }}>
        {isHighScore && <Confetti width={width} height={height} />}
        <Card sx={{ boxShadow: 3, p: 3, backgroundColor: "#fff" }}>
          <CardContent>
            <Typography variant="h4" fontWeight="bold">
              Quiz Completed!
            </Typography>
            <Typography variant="h5" sx={{ mt: 2 }}>
              Your Score: {score} / {questions.length}
            </Typography>
            {isHighScore && (
              <Typography variant="h6" sx={{ mt: 2, color: "#4caf50" }}>
                🎉 Excellent Work!
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/topics")}
                color="info"
              >
                Back to Topics
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => navigate("/")}
              >
                Exit Quiz
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

   return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 5 }}>
        <Card sx={{ boxShadow: 3, p: 2, textAlign: "center", backgroundColor: "#fff" }}>
            <CardContent>
                <Typography variant="h5" fontWeight="bold">
                    Feedback Mode
                </Typography>

                <Typography variant="h6" sx={{ mt: 2 }}>
                    {questions[currentQuestionIndex].question}
                </Typography>

                <Box sx={{ mt: 3 }}>
                    {questions[currentQuestionIndex].options?.length > 0 ? (
                        // Multiple Choice / True-False
                        questions[currentQuestionIndex].options.map((option, i) => {
                            const isCorrectAnswer = option === questions[currentQuestionIndex].correct_answer;
                            const isSelected = option === selectedAnswer;
                            const showFeedback = !!selectedAnswer;

                            let bgColor = "#1976d2";
                            if (showFeedback) {
                                if (isCorrectAnswer) bgColor = "#4caf50";
                                else if (isSelected) bgColor = "#f44336";
                                else bgColor = "#f5f5f5";
                            }

                            let textColor = showFeedback && !isCorrectAnswer && !isSelected ? "black" : "white";
                            let hoverColor = "#1565c0";
                            if (showFeedback) {
                                if (isCorrectAnswer) hoverColor = "#388e3c";
                                else if (isSelected) hoverColor = "#d32f2f";
                                else hoverColor = "#e0e0e0";
                            }

                            return (
                                <Button
                                    key={i}
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        my: 1,
                                        textTransform: "none",
                                        backgroundColor: bgColor,
                                        color: textColor,
                                        "&:hover": {
                                            backgroundColor: hoverColor,
                                        },
                                    }}
                                    disabled={showFeedback}
                                    onClick={() => handleAnswerSelection(option)}
                                >
                                    {option}
                                </Button>
                            );
                        })
                    ) : (
                        // Structured (free-text answer)
                        <>
                            {!selectedAnswer && (
                                <TextField
                                    fullWidth
                                    label="Type your answer"
                                    variant="outlined"
                                    value={typedAnswer}
                                    onChange={(e) => setTypedAnswer(e.target.value)}
                                    sx={{ my: 2 }}
                                />
                            )}
                            {!selectedAnswer && (
                                <Button
                                    variant="contained"
                                    onClick={() => handleAnswerSelection(typedAnswer)}
                                    disabled={!typedAnswer.trim()}
                                >
                                    Submit Answer
                                </Button>
                            )}
                        </>
                    )}
                </Box>

                {selectedAnswer && (
                    <Typography
                        variant="h6"
                        sx={{ mt: 2, fontWeight: "bold", color: isCorrect ? "green" : "red" }}
                    >
                        {isCorrect ? "✅ Correct!" : (
                            <>
                                ❌ Incorrect! The correct answer is:{" "}
                                <span style={{ color: "#4caf50" }}>
                                    {questions[currentQuestionIndex].correct_answer}
                                </span>
                            </>
                        )}
                    </Typography>
                )}

                <Typography variant="h6" sx={{ mt: 3, fontWeight: "bold" }}>
                    Score: {score} / {questions.length}
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
                    <Button variant="outlined" onClick={() => navigate("/categories")} color="info">
                        Back to categories
                    </Button>
                    <Button variant="contained" color="error" onClick={() => navigate("/")}>
                        Exit Quiz
                    </Button>
                </Box>
            </CardContent>
        </Card>
    </Box>
);

};

export default Feedback;
